import { splitReducer } from '@splitsoftware/splitio-redux';
import { localeReducer as locale } from 'react-localize-redux';
import * as Redux from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import createSagaMiddleware from 'redux-saga';
import thunk from 'redux-thunk';

import * as BuildConstants from '../BuildConstants';
import {
  Coordinates,
  Currency,
  LocationCertainty,
} from '../core/api/general/general.types';
import {
  Menu as MenuType,
  MenuItemOptionSetItem,
} from '../core/api/menu/menu.types';
import { OrderHistory, Reorder } from '../core/api/order/order.types';
import {
  CollectionRestaurantDetail,
  CollectionRestaurantSummary,
  DeliveryRestaurantDetail,
  DeliveryRestaurantSummary,
  PickupLocationType,
  PreOrderTime,
  RestaurantDetail,
} from '../core/api/restaurant/restaurant.types';
import { DeliveryLocation } from '../core/api/user/user.types';
import {
  AppConfig,
  AppDesignScheme,
  PhoneNumberConfig,
} from '../core/api/whitelabel/whitelabel.types';
import { generalReducer } from '../core/state/general/generalReducer';
import { NutritionInfoDataDictionary } from '../core/state/menu/menu.types';
import { menuReducer } from '../core/state/menu/menuReducer';
import { Basket } from '../core/state/order/order.types';
import { orderReducer } from '../core/state/order/orderReducer';
import { RestaurantVouchers } from '../core/state/payment/payment.types';
import {
  LoyaltyCampaigns,
  SetCollectionSummaries,
  SetDeliverySummaries,
  SetRestaurantCollection,
  SetRestaurantCollectionLocationAreas,
  SetRestaurantDelivery,
} from '../core/state/restaurant/restaurant.types';
import { restaurantReducer } from '../core/state/restaurant/restaurantReducer';
import {
  AddDeliveryOption,
  DeliveryOption,
  HCaptchaTokens,
  SetDeliveryLocations,
} from '../core/state/user/user.types';
import { userReducer } from '../core/state/user/userReducer';
import { SetAppConfig } from '../core/state/whitelabel/whitelabel.types';
import {
  Async,
  AsyncMap as NewAsyncMap,
  Status,
} from '../core/types/data.types';
import { OrderType } from '../core/types/route.types';
import type { WebOrderTheme } from '../style/themes/base';
import * as Api from './Api';
import * as BasketControl from './BasketControl';
import * as Data from './Data';
import * as OrderFlow from './OrderFlow';
import { defaultState, initSaga, reducers as newAgeReducers } from './redux';
import * as SettingsStore from './SettingsStore';
import { Utilities } from './Utilities';
import * as Vouchers from './Vouchers';

export interface LocationGuess {
  certainty: LocationCertainty;
  geo: Coordinates;
}

export interface ExpandedStates {
  [vrid: number]: number[];
}

export interface CardOrder {
  vrid: number;
  prid: number;
  chefNote: string;
  deliveryTime: string;
  type: 'collection' | 'delivery';
  id: number;
  currency: Currency;
  total: number;
}

// For storing Chef Notes
export interface ChefNotes {
  // TODO:DN
  [menuId: number]: string | undefined;
}

// For storing Table Numbers
export interface TableNumbers {
  [vrid: number]: string | undefined;
}

// For storing Pickup Location Type
export interface PickupLocationTypes {
  [vrid: number]: PickupLocationType | undefined;
}

// ! Deprecated - use AsyncMap from src/core/types/data.types.d.ts
export interface AsyncMap<K> {
  [key: string]: Data.Async<K> | undefined;
}

export const DeliveryKey = (vrid: number, dlid: number): string => {
  const key = `del-${vrid}-${dlid}`;
  return key;
};

export interface FlipdishState {
  addressSuggestion: { suggestion: string; postalCode: any };
  AfterLogin: string; // Where to navigate to after login.
  AppConfig: Async<AppConfig>;
  FallbackBaseUrl?: string;
  CanCancelOrder: boolean;
  CardOrder: CardOrder | null;
  ChefNotes: ChefNotes;
  ClientAnalytics: boolean;
  CollectionSummaries: Data.Async<CollectionRestaurantSummary[]>;
  Config: Data.Config;
  CountryCode: string;
  Customer: Data.Async<Api.Customer>;
  DeliveryLocations: Async<DeliveryLocation[]>;
  DeliverySummaries: Data.Async<DeliveryRestaurantSummary[]>;
  ExpandedStates: ExpandedStates;
  isDark: boolean;
  LastRestaurantUrl: string;
  Location: LocationGuess;
  MenuScrollY: number;
  NewSectionCreating: boolean;
  Offset: number;
  OptionsState: {
    Options: MenuItemOptionSetItem[];
    PrevScreenOptions: MenuItemOptionSetItem[][];
    Current: number;
    PrevIndices: number[];
  } | null;
  Order: OrderFlow.State;
  PhoneNumberConfig: PhoneNumberConfig;
  PickupLocationTypes: PickupLocationTypes;
  preselectedStoreVrid: number;
  showAddCardDialog: boolean;
  TableNumbers: TableNumbers;
  themeName: WebOrderTheme;
  ToastMessages: string[];
  VouchersInProgress: number;
  WebOrderUiVersion?: string;
}

export interface IntercomSettings {
  app_id: string | null;
  user_id: string | null;
  email: string | null;
  name: string | null;
  whitelabelid: number | null;
  flipdishuserid: number | null;
  hide_default_launcher: true;
  promotions_enabled: boolean | null;
  phone: string | null;
}

const EmptyIntercom: IntercomSettings = {
  app_id: null,
  user_id: null,
  email: null,
  name: null,
  whitelabelid: null,
  flipdishuserid: null,
  promotions_enabled: null,
  hide_default_launcher: true,
  phone: null,
};

export interface Language {
  code: string;
  active: boolean;
}

export interface LocaleOptions {
  renderInnerHtml: boolean;
  showMissingTranslationMsg: boolean;
  defaultLanguage?: string;
}

export interface LocaleSettings {
  languages: Language[];
  translations: object;
  options: LocaleOptions;
}

const EmptyLocale: LocaleSettings = {
  languages: [],
  translations: {},
  options: {
    renderInnerHtml: true,
    showMissingTranslationMsg: true,
  },
};

export const DefaultAppDesignScheme: AppDesignScheme = {
  Primary: BuildConstants.PRIMARY_COLOR,
  Secondary: BuildConstants.THEME_COLOR,
  Tertiary: BuildConstants.TERTIARY_COLOR,
};

// #region GLOBAL APP TYPES
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace WebOrder {
    interface State {
      appDesignScheme: AppDesignScheme;
      basket: Basket;
      collectionRestaurants: NewAsyncMap<CollectionRestaurantDetail>;
      cookieConsent: Readonly<State.CookieConsent>;
      deliveryOptions: NewAsyncMap<DeliveryOption>; // keyed by delivery location id
      deliveryRestaurants: NewAsyncMap<DeliveryRestaurantDetail>;
      flipdish: FlipdishState;
      general: Readonly<State.General>;
      intercom: IntercomSettings;
      locale: any;
      location: Readonly<State.Location>;
      menu: Readonly<State.Menu>;
      orders: Readonly<State.Order>;
      payments: Readonly<State.Payments>;
      restaurant: Readonly<State.Restaurant>;
      splitio: any;
      user: Readonly<State.User>;
    }
    // eslint-disable-next-line @typescript-eslint/no-namespace
    namespace State {
      interface General {
        afterProfilePanelUrl: string;
      }

      interface User {
        hCaptchaTokens: HCaptchaTokens;
        loginErrorMessage?: string;
      }

      interface Restaurant {
        loyaltyCampaigns: LoyaltyCampaigns;
      }

      interface Location {
        pathname: string;
        params?: {
          backurl?: string;
          concessionStoreId?: string;
          dlid?: string;
          id?: string;
          index?: string;
          item?: string;
          lat?: string;
          lng?: string;
          location?: string;
          orderId?: string;
          paymentAccount?: string;
          phone?: string;
          prid?: string;
          type?: OrderType;
          vrid?: string;
        };
        search?: string;
      }

      interface Menu {
        menus: NewAsyncMap<MenuType>;
        nutritionInfos: NewAsyncMap<NutritionInfoDataDictionary>;
      }

      interface Order {
        history: Data.Async<OrderHistory[]>;
        reorders: {
          collectionReorders: Async<Reorder[]>;
          deliveryReorders: Async<Reorder[]>;
          restaurantCollectionReorders: { [prId: string]: Async<Reorder[]> };
          restaurantDeliveryReorders: { [prId: string]: Async<Reorder[]> };
        };
        reorderOrderId?: number;
        unavailableReorderItems: string[];
      }

      interface Payment extends Api.PaymentAccount {
        paymentId: string;
        displayOrder: number;
        status: 'none' | 'pending' | 'success' | 'error';
      }

      interface Payments {
        canApplyRestaurantVoucher?: boolean;
        status: 'none' | 'pending' | 'success';
        items: {
          [paymentId: string]: Payment;
        };
        restaurantVouchers?: AsyncMap<RestaurantVouchers>;
        error?: Error;
      }
      interface CookieConsent {
        selectedGroups: Array<
          'functional' | 'necessary' | 'performance' | 'advertising'
        >;
        rememberMe: boolean;
      }
    }
  }
}
// #endregion
export type State = WebOrder.State;

function Empty(config: Data.Config): FlipdishState {
  return {
    addressSuggestion: { suggestion: '', postalCode: '' },
    AfterLogin: '',
    AppConfig: {
      status: Status.LOADING,
    },
    CanCancelOrder: false,
    CardOrder: null,
    ChefNotes: {},
    ClientAnalytics: false,
    CollectionSummaries: {
      status: 'pending',
    },
    Config: config,
    CountryCode: '',
    Customer: {
      status: 'pending',
    },
    DeliveryLocations: {
      status: Status.LOADING,
    },
    DeliverySummaries: {
      status: 'pending',
    },
    ExpandedStates: {},
    isDark: false,
    LastRestaurantUrl: '/',
    Location: {
      certainty: LocationCertainty.Hardcoded,
      geo: {
        Latitude: 53.3454362,
        Longitude: -6.2616217,
      },
    },
    MenuScrollY: 0,
    NewSectionCreating: false,
    Offset: 0,
    OptionsState: {
      Options: [],
      PrevScreenOptions: [[]],
      Current: 0,
      PrevIndices: [],
    },
    Order: {
      state: 'none',
      submitType: 'NORMAL',
    },
    PhoneNumberConfig: {
      // This is a valid prefix that will cause the phone number widget to be
      // 'dumb' - no auto prefix filled in, no auto-submit.
      Prefix: '',
      AutocompletePhoneNumberLength: 10000,
    },
    PickupLocationTypes: {},
    preselectedStoreVrid: undefined,
    showAddCardDialog: false,
    TableNumbers: {},
    themeName: 'loading',
    ToastMessages: [],
    VouchersInProgress: 0,
    WebOrderUiVersion: '',
  };
}

export interface ClearFlipdishStore {
  type: 'CLEAR_FLIPDISH_STORE';
}

// #region Collection Restaurants----------------------------------------------------------
export interface SetRestaurantCollectionPreOrderTimes {
  type: 'SET_RESTAURANT_COLLECTION_PRE_ORDER_TIMES';
  payload: {
    vrId: number;
    prId: number;
    preOrderTimes: PreOrderTime[];
  };
}

export interface SetRestaurantCollectionTableLock {
  type: 'SET_RESTAURANT_COLLECTION_TABLE_LOCK';
  payload: {
    prid: number;
    isTableLockEnabled: boolean;
  };
}

const collectionReducer = (
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: NewAsyncMap<CollectionRestaurantDetail> = {},
  action:
    | SetRestaurantCollection
    | SetRestaurantCollectionPreOrderTimes
    | SetRestaurantCollectionLocationAreas
    | SetRestaurantCollectionTableLock,
): NewAsyncMap<CollectionRestaurantDetail> => {
  switch (action.type) {
    case 'SET_RESTAURANT_COLLECTION': {
      return {
        ...state,
        [action.payload.prid]: {
          ...action.payload.restaurant,
          data: {
            // @ts-ignore 'Pending' state doesn't have data prop
            ...state[action.payload.prid]?.data,
            // @ts-ignore 'Pending' state doesn't have data prop
            ...action.payload.restaurant.data,
          },
        },
      };
    }

    case 'SET_RESTAURANT_COLLECTION_PRE_ORDER_TIMES': {
      const key = action.payload.prId;

      // just in case
      if (!state[key] || state[key].status !== 'success') {
        return state;
      }

      return {
        ...state,
        [key]: {
          ...state[key],
          data: {
            // @ts-ignore 'Pending' state doesn't have data prop
            ...state[key].data,
            PreOrderOptions: {
              // @ts-ignore 'Pending' state doesn't have data prop
              ...state[key].data.PreOrderOptions,
              PreOrderTimes: action.payload.preOrderTimes,
            },
          },
        },
      };
    }

    case 'SET_RESTAURANT_COLLECTION_LOCATION_AREAS': {
      return {
        ...state,
        [action.payload.prId]: {
          // @ts-ignore 'Pending' state doesn't have data prop
          ...state[action.payload.prId],
          data: {
            // @ts-ignore 'Pending' state doesn't have data prop
            ...state[action.payload.prId]?.data,
            LocationAreas: action.payload.locationAreas,
          },
        },
      };
    }

    case 'SET_RESTAURANT_COLLECTION_TABLE_LOCK': {
      return {
        ...state,
        [action.payload.prid]: {
          // @ts-ignore 'Pending' state doesn't have data prop
          ...state[action.payload.prid],
          data: {
            // @ts-ignore 'Pending' state doesn't have data prop
            ...state[action.payload.prid]?.data,
            IsTableLockEnabled: action.payload.isTableLockEnabled,
          },
        },
      };
    }

    default:
      return state;
  }
};
// #endregion

// #region Delivery Restaurants----------------------------------------------------------------
export interface SetRestaurantDeliveryPreOrderTimes {
  type: 'SET_RESTAURANT_DELIVERY_PRE_ORDER_TIMES';
  payload: {
    vrId: number;
    dlId: number;
    preOrderTimes: PreOrderTime[];
  };
}

const deliveryReducer = (
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: NewAsyncMap<DeliveryRestaurantDetail> = {},
  action: SetRestaurantDelivery | SetRestaurantDeliveryPreOrderTimes,
): NewAsyncMap<DeliveryRestaurantDetail> => {
  switch (action.type) {
    case 'SET_RESTAURANT_DELIVERY': {
      const key = DeliveryKey(action.payload.vrid, action.payload.dlid);
      return {
        ...state,
        [key]: action.payload.restaurant,
      };
    }

    case 'SET_RESTAURANT_DELIVERY_PRE_ORDER_TIMES': {
      const key = DeliveryKey(action.payload.vrId, action.payload.dlId);

      // just in case
      if (!state[key] || state[key].status !== 'success') {
        return state;
      }

      return {
        ...state,
        [key]: {
          ...state[key],
          // @ts-ignore 'Pending' state doesn't have data prop
          data: {
            // @ts-ignore 'Pending' state doesn't have data prop
            ...state[key].data,
            PreOrderOptions: {
              // @ts-ignore 'Pending' state doesn't have data prop
              ...state[key].data.PreOrderOptions,
              PreOrderTimes: action.payload.preOrderTimes,
            },
          },
        },
      };
    }

    default:
      return state;
  }
};
// #endregion

// #region Deferred Menus--------------------------------------------------------------------------

export interface AddToDeferredMenus {
  type: 'ADD_TO_DEFERRED_MENUS';
  payload: RestaurantDetail;
}

export interface StartDeferredMenusLoading {
  type: 'START_DEFERRED_MENUS_LOADING';
}

export interface FinishDeferredMenuLoading {
  type: 'FINISH_DEFERRED_MENU_LOADING';
}

export type DeferredMenuAction =
  | AddToDeferredMenus
  | StartDeferredMenusLoading
  | FinishDeferredMenuLoading;
// #endregion

// #region all together
export interface SetAppDesignScheme {
  type: 'SET_APP_DESIGN_SCHEME';
  payload: AppDesignScheme;
}

export interface IncrementVouchersInProgress {
  type: 'INCREMENT_VOUCHERS_IN_PROGRESS';
}

export interface DecrementVouchersInProgress {
  type: 'DECREMENT_VOUCHERS_IN_PROGRESS';
}

export interface SetCardOrder {
  type: 'SET_CARD_ORDER';
  payload: CardOrder | null;
}

export interface SetClientAnalytics {
  type: 'SET_CLIENT_ANALYTICS';
  payload: boolean;
}

export interface SetCanCancel {
  type: 'SET_CAN_CANCEL';
  payload: boolean;
}

export interface SetOrder {
  type: 'SET_ORDER';
  payload: OrderFlow.State;
}

export interface SetFallbackBaseUrl {
  type: 'SET_FALLBACK_BASE_URL';
  payload: string;
}

export interface SetTheme {
  type: 'SET_THEME';
  payload: WebOrderTheme;
}

export interface SetIsDarkTheme {
  type: 'SET_IS_DARK_THEME';
  payload: boolean;
}

export interface SetOffset {
  type: 'SET_OFFSET';
  payload: number;
}

export interface SetOptionsState {
  type: 'SET_OPTIONS_STATE';
  payload: {
    Options: MenuItemOptionSetItem[];
    //  OptionsBeforeNextScreen: Array<Api.MenuItemOptionSetItem>,
    PrevScreenOptions: MenuItemOptionSetItem[][];
    Current: number;
    PrevIndices: number[];
  };
}

export interface SetLiveOptions {
  type: 'SET_LIVE_OPTIONS';
  payload: MenuItemOptionSetItem[];
}

export interface SetNextOptions {
  type: 'SET_NEXT_OPTIONS';
  payload: {
    PrevScreenOptions: MenuItemOptionSetItem[][];
    Current: number;
    PrevIndices: number[];
  };
}

export interface LoadBasket {
  type: 'LOAD_BASKET';
  payload: BasketControl.Data;
}
export interface AddToBasket {
  type: 'ADD_TO_BASKET';
  payload: BasketControl.Item;
}
export interface RemoveFromBasket {
  type: 'REMOVE_FROM_BASKET';
  payload: BasketControl.Item;
}
export interface ClearBasket {
  type: 'CLEAR_BASKET';
  payload: BasketControl.Data;
}
export interface SetTip {
  type: 'BASKET_SET_TIP';
  payload: number;
}

export interface AddAddressSuggestion {
  type: 'ADD_ADDRESS_SUGGESTION';
  payload: {
    suggestion: string;
    postalCode: any;
  };
}

export interface SetVoucher {
  type: 'SET_VOUCHER';
  payload: {
    voucher: Vouchers.States;
    menuId: number;
  };
}

export interface SetRequestedTime {
  type: 'SET_REQUESTED_TIME';
  payload: {
    time: string;
    menuId: number;
  };
}

export interface SetShowAddCardDialog {
  type: 'SET_SHOW_ADD_CARD_DIALOG';
  payload: boolean;
}

export interface InitializeExpandedState {
  type: 'INITIALIZE_EXPANDED_STATE';
  payload: {
    vrid: number;
    expandedState: number[];
  };
}

export interface AddToExpandedState {
  type: 'ADD_TO_EXPANDED_STATE';
  payload: {
    menuSectionId: number;
    vrid: number;
  };
}

export interface RemoveFromExpandedState {
  type: 'REMOVE_FROM_EXPANDED_STATE';
  payload: {
    menuSectionId: number;
    vrid: number;
  };
}

export interface EditChefNotes {
  type: 'EDIT_CHEF_NOTES';
  payload: {
    value: string;
    vrid: number;
  };
}

export interface EditPickupLocationTypes {
  type: 'EDIT_PICKUP_LOCATION_TYPES';
  payload: {
    value: PickupLocationType;
    vrid: number;
  };
}

export interface EditTableNumbers {
  type: 'EDIT_TABLE_NUMBERS';
  payload: {
    value: number;
    vrid: number;
  };
}

export interface LoadPhoneNumberConfig {
  type: 'LOAD_PHONE_NUMBER_CONFIG';
  payload: PhoneNumberConfig;
}

export interface SetCountryCode {
  type: 'SET_COUNTRY_CODE';
  payload: string;
}

export interface AddLocation {
  type: 'ADD_LOCATION';
  payload: LocationGuess;
}

export interface ClearDeliveryLocations {
  type: 'CLEAR_DELIVERY_LOCATIONS';
}
export interface AddToast {
  type: 'ADD_TOAST';
  payload: string;
}

export interface RemoveToast {
  type: 'REMOVE_TOAST';
  payload: string;
}

export interface PushLoginUrl {
  type: 'PUSH_LOGIN_URL';
  payload: string;
}

export interface SetRestaurantUrl {
  type: 'SET_RESTAURANT_URL';
  payload: string;
}
export interface setPreselectedStoreVrid {
  type: 'SET_PRESELECTED_STORE_VRID';
  payload: number;
}

export interface SetCustomer {
  type: 'SET_CUSTOMER';
  payload: Data.Async<Api.Customer>;
}
export interface SetCustomerName {
  type: 'SET_CUSTOMER_NAME';
  payload: { name: string };
}

export interface SetSmsEnabled {
  type: 'SET_SMS_ENABLED';
  payload: boolean;
}

export interface SetMenuScroll {
  type: 'SET_MENU_SCROLL';
  payload: number;
}

export const appDesignSchemeReducer = (
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: AppDesignScheme = DefaultAppDesignScheme,
  action: SetAppDesignScheme,
): AppDesignScheme => {
  switch (action.type) {
    case 'SET_APP_DESIGN_SCHEME': {
      return {
        ...state,
        ...action.payload,
      };
    }
    default:
      return state;
  }
};

const deliveryOptionsReducer = (
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: NewAsyncMap<DeliveryOption> = {},
  action: AddDeliveryOption | SetDeliveryLocations,
) => {
  switch (action.type) {
    case 'ADD_DELIVERY_OPTION': {
      return {
        ...state,
        [action.payload.dlid]: {
          status: Status.SUCCESS,
          data: action.payload.option,
        },
      };
    }

    case 'SET_DELIVERY_LOCATIONS': {
      const result = { ...state };
      const data =
        action.payload.status === Status.SUCCESS ? action.payload.data : [];

      data.forEach((dl) => {
        if (!result[dl.DeliveryLocationId]) {
          result[dl.DeliveryLocationId] = { status: Status.LOADING };
        }
      });

      return result;
    }
    default:
      return state;
  }
};
// #endregion

// #region flipdishReducer
export type Action =
  | AddAddressSuggestion
  | AddLocation
  | AddToast
  | AddToBasket
  | AddToExpandedState
  | ClearBasket
  | ClearDeliveryLocations
  | ClearFlipdishStore
  | DecrementVouchersInProgress
  | EditChefNotes
  | EditPickupLocationTypes
  | EditTableNumbers
  | IncrementVouchersInProgress
  | InitializeExpandedState
  | LoadBasket
  | LoadPhoneNumberConfig
  | PushLoginUrl
  | RemoveFromBasket
  | RemoveFromExpandedState
  | RemoveToast
  | SetAppConfig
  | SetAppDesignScheme
  | SetCanCancel
  | SetCardOrder
  | SetClientAnalytics
  | SetCollectionSummaries
  | SetCountryCode
  | SetCustomer
  | SetCustomerName
  | SetDeliveryLocations
  | SetDeliverySummaries
  | SetFallbackBaseUrl
  | SetIsDarkTheme
  | SetLiveOptions
  | SetMenuScroll
  | SetNextOptions
  | SetOffset
  | SetOptionsState
  | SetOrder
  | setPreselectedStoreVrid
  | SetRequestedTime
  | SetRestaurantUrl
  | SetShowAddCardDialog
  | SetSmsEnabled
  | SetTheme
  | SetTip
  | SetVoucher;

const reducer = (config: Data.Config) => {
  const defaultConfig = Empty(config);

  return (s: FlipdishState, action: Action): FlipdishState => {
    let state = { ...s };
    if (!state) {
      state = defaultConfig;
    }

    switch (action.type) {
      case 'CLEAR_FLIPDISH_STORE': {
        return {
          ...defaultConfig,
          OptionsState: null,
        };
      }

      case 'INCREMENT_VOUCHERS_IN_PROGRESS': {
        return {
          ...state,
          VouchersInProgress: state.VouchersInProgress + 1,
        };
      }

      case 'DECREMENT_VOUCHERS_IN_PROGRESS': {
        return {
          ...state,
          VouchersInProgress: state.VouchersInProgress - 1,
        };
      }

      case 'SET_CARD_ORDER': {
        return { ...state, CardOrder: action.payload };
      }

      case 'SET_CLIENT_ANALYTICS': {
        return { ...state, ClientAnalytics: action.payload };
      }

      case 'SET_CAN_CANCEL': {
        return { ...state, CanCancelOrder: action.payload };
      }

      case 'SET_MENU_SCROLL': {
        return { ...state, MenuScrollY: action.payload };
      }

      case 'SET_COLLECTION_SUMMARIES': {
        return { ...state, CollectionSummaries: action.payload };
      }

      case 'SET_SHOW_ADD_CARD_DIALOG': {
        return { ...state, showAddCardDialog: action.payload };
      }

      case 'SET_DELIVERY_SUMMARIES': {
        return { ...state, DeliverySummaries: action.payload };
      }

      case 'SET_DELIVERY_LOCATIONS': {
        return { ...state, DeliveryLocations: action.payload };
      }

      case 'LOAD_PHONE_NUMBER_CONFIG': {
        return {
          ...state,
          PhoneNumberConfig: action.payload,
        };
      }

      case 'SET_APP_CONFIG': {
        const data =
          action.payload.status === Status.SUCCESS
            ? action.payload.data
            : undefined;

        return {
          ...state,
          PhoneNumberConfig: data?.PhoneNumberConfig,
          AppConfig: action.payload,
        };
      }

      case 'SET_COUNTRY_CODE': {
        return {
          ...state,
          CountryCode: action.payload,
        };
      }

      case 'ADD_LOCATION': {
        // Only accept updates from higher priority sources.
        if (state.Location.certainty > action.payload.certainty) {
          return state;
        }
        try {
          SettingsStore.LocationStore.Save(action.payload.geo);
        } catch (e) {
          console.log(e.message);
        }
        return { ...state, Location: action.payload };
      }

      case 'ADD_TOAST': {
        return {
          ...state,
          ToastMessages: [...state.ToastMessages, action.payload],
        };
      }

      case 'ADD_ADDRESS_SUGGESTION': {
        return { ...state, addressSuggestion: action.payload };
      }

      case 'REMOVE_TOAST': {
        const [...rest] = state.ToastMessages;
        const remainingToasts = rest.filter((a) => a !== action.payload);

        return { ...state, ToastMessages: remainingToasts };
      }

      case 'PUSH_LOGIN_URL': {
        return { ...state, AfterLogin: action.payload };
      }

      case 'SET_RESTAURANT_URL': {
        return { ...state, LastRestaurantUrl: action.payload };
      }

      case 'SET_PRESELECTED_STORE_VRID': {
        return { ...state, preselectedStoreVrid: action.payload };
      }

      case 'SET_CUSTOMER': {
        return { ...state, Customer: action.payload };
      }

      case 'CLEAR_DELIVERY_LOCATIONS': {
        return {
          ...state,
          DeliveryLocations: {
            data: [],
            status: Status.SUCCESS,
          },
        };
      }

      case 'SET_CUSTOMER_NAME': {
        return {
          ...state,
          Customer: {
            ...state.Customer,
            // @ts-ignore unresolved
            data: {
              // @ts-ignore unresolved
              ...(state.Customer.data || {}),
              CustomerName: action.payload.name,
            },
          },
        };
      }

      case 'SET_FALLBACK_BASE_URL': {
        return { ...state, FallbackBaseUrl: action.payload };
      }

      case 'SET_THEME': {
        return { ...state, themeName: action.payload };
      }

      case 'SET_IS_DARK_THEME': {
        return { ...state, isDark: action.payload };
      }

      case 'SET_OFFSET': {
        return { ...state, Offset: action.payload };
      }

      case 'SET_LIVE_OPTIONS': {
        return {
          ...state,
          OptionsState: { ...state.OptionsState, Options: action.payload },
        };
      }

      case 'SET_NEXT_OPTIONS': {
        return {
          ...state,
          OptionsState: {
            ...state.OptionsState,
            PrevIndices: action.payload.PrevIndices,
            Current: action.payload.Current,
            PrevScreenOptions: action.payload.PrevScreenOptions,
          },
        };
      }

      case 'SET_OPTIONS_STATE': {
        return { ...state, OptionsState: action.payload };
      }

      case 'SET_SMS_ENABLED': {
        const c = state.Customer;
        if (c.status !== 'success') {
          return state;
        }
        return {
          ...state,
          Customer: {
            ...state.Customer,
            data: {
              ...c.data,
              SmsPromotionsEnabled: action.payload,
            },
          },
        } as any;
      }

      case 'INITIALIZE_EXPANDED_STATE': {
        const { vrid, expandedState } = action.payload;
        return {
          ...state,
          ExpandedStates: {
            ...state.ExpandedStates,
            [vrid]: expandedState,
          },
        };
      }

      case 'ADD_TO_EXPANDED_STATE': {
        const { menuSectionId, vrid } = action.payload;

        return {
          ...state,
          ExpandedStates: {
            ...state.ExpandedStates,
            [vrid]: state.ExpandedStates[vrid].concat([menuSectionId]),
          },
        };
      }

      case 'REMOVE_FROM_EXPANDED_STATE': {
        const { menuSectionId, vrid } = action.payload;

        return {
          ...state,
          ExpandedStates: {
            ...state.ExpandedStates,
            [vrid]: Utilities.findAndRemove(
              state.ExpandedStates[vrid],
              menuSectionId,
            ),
          },
        };
      }

      case 'SET_ORDER': {
        return { ...state, Order: action.payload };
      }

      default:
        return state;
    }
  };
};
// #endregion

// #region intercomReducer
const intercomReducer = (
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: IntercomSettings = EmptyIntercom,
  action: Action,
) => {
  const withUserIdAdded = (s: IntercomSettings) => {
    if (!s.whitelabelid || !s.flipdishuserid) {
      return s;
    }
    return {
      ...s,
      user_id: `${s.whitelabelid}-${s.flipdishuserid}`,
    };
  };

  switch (action.type) {
    case 'SET_APP_CONFIG': {
      const data =
        action.payload.status === Status.SUCCESS
          ? action.payload.data
          : undefined;

      return withUserIdAdded({
        ...state,
        app_id: data?.IntercomAppId,
        whitelabelid: data?.WhiteLabelId,
      });
    }

    case 'SET_CUSTOMER': {
      const { payload } = action;
      if (payload.status !== 'success') {
        return state;
      }
      const { data } = payload;
      return withUserIdAdded({
        ...state,
        flipdishuserid: data.UserId,
        name: data.CustomerName,
        email: data.EmailAddress,
        phone: data.PhoneNumber,
        promotions_enabled: data.SmsPromotionsEnabled,
      });
    }

    default:
      return state;
  }
};
// #endregion

// #region setup & init
const sagaMiddleware = createSagaMiddleware();
const middlewares = [sagaMiddleware, thunk];

const middleware = Redux.applyMiddleware(...middlewares);

interface result {
  store: Redux.Store<State>;
}

export const watchForLocationChanges = (
  store: Redux.Store<State>,
  then: (location: Coordinates) => any,
) => {
  const sameLocation = (a: LocationGuess, b: LocationGuess): boolean =>
    a.certainty === b.certainty &&
    a.geo.Latitude === b.geo.Latitude &&
    a.geo.Longitude === b.geo.Longitude;

  let { Location } = store.getState().flipdish;

  store.subscribe(() => {
    const NextLocation = store.getState().flipdish.Location;

    if (!sameLocation(NextLocation, Location)) {
      then(NextLocation.geo);
      Location = NextLocation;
    }
  });
};

let storeInstance: Redux.Store<State, Redux.Action<any>>;

export const Init = (config: Data.Config, initialState?: any): result => {
  const reducers = Redux.combineReducers<WebOrder.State>({
    appDesignScheme: appDesignSchemeReducer,
    collectionRestaurants: collectionReducer,
    deliveryOptions: deliveryOptionsReducer,
    deliveryRestaurants: deliveryReducer,
    flipdish: reducer(config),
    general: generalReducer,
    intercom: intercomReducer,
    locale,
    menu: menuReducer,
    orders: orderReducer,
    restaurant: restaurantReducer,
    splitio: splitReducer,
    user: userReducer,
    ...newAgeReducers,
  });

  const store = Redux.createStore<State, Redux.Action<any>, {}, {}>(
    reducers,
    initialState || {
      appDesignScheme: DefaultAppDesignScheme,
      collectionRestaurants: {},
      deliveryOptions: {},
      deliveryRestaurants: {},
      general: {
        afterProfilePanelUrl: '/',
      },
      flipdish: Empty(config),
      intercom: EmptyIntercom,
      locale: EmptyLocale,
      orders: {
        history: {
          data: [],
          status: 'pending',
        },
        reorders: {
          collectionReorders: { status: Status.LOADING },
          deliveryReorders: { status: Status.LOADING },
          restaurantCollectionReorders: {},
          restaurantDeliveryReorders: {},
        },
        unavailableReorderItems: [],
      },
      ...defaultState,
    },
    composeWithDevTools(middleware),
  );

  initSaga(sagaMiddleware);

  storeInstance = store;

  return {
    store,
  };
};

export function getStoreInstance() {
  return storeInstance;
}
// #endregion
